/* global axios */

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

export const state = {
  branches: [],
  pagination: {
    page: 1,
    total: 0,
    next: null,
    page_size: 10,
  },
  selectedBranch: null,
}

export const getters = {
  formattedPagination: (state) => {
    const { page, total, next, page_size } = state.pagination;

    return {
      currentPage: page,
      totalPages: Math.max(1, Math.ceil(total / page_size)),
      nextPage: next,
      pageSize: page_size,
    };
  },
}

export const mutations = {
  SET_BRANCHES(state, branches) {
    state.branches = branches;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination };
  },
  SET_SELECTED_BRANCH(state, branch) {
    state.selectedBranch = branch;
  },
}

export const actions = {
  async fetchBranches({ commit, rootGetters, state }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches`),
        { params: { page: state.pagination.page, page_size: state.pagination.page_size } }
      );
      const { results, page: currentPage, total, next } = res.data;
      commit('SET_BRANCHES', results);
      commit('SET_PAGINATION', { ...state.pagination, page: currentPage, total, next });
      return res.data;
    } catch (error) {
      console.error('Fetch branches failed:', error);
      throw error.response?.data || error;
    }
  },
  
  updatePagination({ commit, dispatch, state }, newPagination = {}) {
    commit('SET_PAGINATION', { ...state.pagination, ...newPagination });
    return dispatch('fetchBranches');
  },

  async createBranch({ rootGetters }, branchData) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches`),
        branchData
      );
      return res.data;
    } catch (error) {
      console.error('Create branch failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateBranch({ rootGetters }, { branchId, branchData }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}`),
        branchData
      );
      return res.data;
    } catch (error) {
      console.error('Update branch failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteBranch({ rootGetters }, branchId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete branch failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchActiveBranchCount({ rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/active_branch_count`)
      );
      return res.data.count;
    } catch (error) {
      console.error('Fetch active branch count failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchActiveBranches({ rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/active_branches`)
      );
      return res.data.results;
    } catch (error) {
      console.error('Fetch active branches failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateActiveBranches({ rootGetters }, selectedBranchIds) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/subscription/${accountId}/active_branches`),
        { ids: selectedBranchIds }
      );
      return res.data;
    } catch (error) {
      console.error('Update active branches failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchAllBranches({ rootGetters, commit }) {
    const accountId = rootGetters.getCurrentAccountId;
    const allBranches = [];
    const pageSize = 100;
    let page = 1;
    let totalPages = 1;

    try {
      do {
        const res = await axios.get(
          knowledgeProxy(accountId, `/accounts/${accountId}/branches`),
          { params: { page, page_size: pageSize } }
        );

        const { results, total } = res.data;
        allBranches.push(...results);

        if (page === 1) {
          totalPages = Math.ceil(total / pageSize);
        }

        page++;
      } while (page <= totalPages);

      commit('SET_BRANCHES', allBranches);
      return allBranches;
    } catch (error) {
      console.error('Fetch all branches failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchBranch({ rootGetters, commit }, branchId) {
    if (!branchId) {
      throw new Error('Branch ID is required');
    }

    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}`)
      );

      const data = res.data;
      commit('SET_SELECTED_BRANCH', data);
      return data;
    } catch (error) {
      console.error('Fetch branch failed:', error);
      throw error.response?.data || error;
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
